import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TrendingUp, ShoppingCart, Trophy } from 'lucide-react';
import { useDateRangeStore } from '@/stores/dateRangeStore';
import { salesApi } from '@/services/api';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { KPICard } from '@/components/dashboard/KPICard';
import { TopAgentMonthCard } from '@/components/dashboard/TopAgentMonthCard';
import { DateRangeSelector } from '@/components/dashboard/DateRangeSelector';
import { SalesChart } from '@/components/dashboard/SalesChart';
import type { SalesSummary } from '@/types';

export function Dashboard() {
  const { t, i18n } = useTranslation();
  const { preset, startDate, endDate } = useDateRangeStore();

  const { data: summary, isLoading } = useQuery<SalesSummary>({
    queryKey: ['sales-summary', preset, startDate.toISOString(), endDate.toISOString()],
    queryFn: () =>
      salesApi.getSummary({
        preset,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      }),
    refetchInterval: 60000, // Refetch every minute
  });

  const topAgent = summary?.agentSummaries?.[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        <DateRangeSelector />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title={t('dashboard.totalSales')}
          value={formatCurrency(summary?.totalSales || 0, i18n.language)}
          icon={TrendingUp}
          isLoading={isLoading}
          trend={12.5}
          color="blue"
        />
        <KPICard
          title={t('dashboard.transactionCount')}
          value={formatNumber(summary?.transactionCount || 0, i18n.language)}
          icon={ShoppingCart}
          isLoading={isLoading}
          color="green"
        />
        <KPICard
          title={t('dashboard.topAgent')}
          value={topAgent?.agentName || '-'}
          subtitle={topAgent ? formatCurrency(topAgent.totalSales, i18n.language) : undefined}
          icon={Trophy}
          isLoading={isLoading}
          color="purple"
        />
        <TopAgentMonthCard />
      </div>

      {/* Sales Chart */}
      <SalesChart />
    </div>
  );
}
